import { createSelector } from 'reselect';

import { DEFAULT_CHECK } from 'Lib/constants/detection';
import { createKeySelector } from 'Lib/reduxUtils';

import { ThemeId } from 'Store/appSettings/types';
import { selectContext } from 'Store/context/selectors';

import { CHAT_SUGGESTION_CONSENT_ACCEPTED } from 'Containers/ChatSuggestionsConsent/constants';
import DEFAULT_MERCHANDISING_CARDS from 'Containers/SubscriptionMerchandisingPage/common/constants';
import {
  STATUS_FAILURE,
  STATUS_REVIEWING,
  STATUS_START,
  STATUS_UNSUCCESSFUL,
} from 'Components/AgeVerification/constants';

const NEEDED_STATUSES = new Set([
  STATUS_START,
  STATUS_REVIEWING,
  STATUS_UNSUCCESSFUL,
  STATUS_FAILURE,
]);

export const selectAppSettings = createKeySelector('appSettings');

export const getSuperBoostMembersText = createSelector(
  selectAppSettings,
  (appSettings) => appSettings.superBoostMembersText
);
export const getSuperlikeAlcMode = createSelector(
  selectAppSettings,
  (appSettings) => appSettings.superlikeAlcMode
);

export const getCanEditEmail = createSelector(
  selectAppSettings,
  (appSettings) => appSettings.canEditEmail
);

export const getCompanyMaxLength = createSelector(
  selectAppSettings,
  (appSettings) => appSettings.companyMaxLength
);

export const getJobTitleMaxLength = createSelector(
  selectAppSettings,
  (appSettings) => appSettings.jobTitleMaxLength
);

export const getSchoolMaxLength = createSelector(
  selectAppSettings,
  (appSettings) => appSettings.schoolMaxLength
);

export const getLayout = createSelector(selectAppSettings, (appSettings) => appSettings.layout);

export const getIsMobileLayout = createSelector(
  selectAppSettings,
  (appSettings) => appSettings.layout === 'mobile'
);

export const getIsRecsToolbarOpened = createSelector(
  selectAppSettings,
  (appSettings) => appSettings.recsToolbarIsOpened
);

export const getTypingPulseInterval = createSelector(
  selectAppSettings,
  (appSettings) => appSettings.typingPulseInterval
);

export const getTypingTimeout = createSelector(
  selectAppSettings,
  (appSettings) => appSettings.typingTimeout
);

export const getShouldShowTermsUpdate = createSelector(
  selectAppSettings,
  (appSettings) => appSettings.showTerms
);

export const getTermsVersion = createSelector(
  selectAppSettings,
  (appSettings) => appSettings.termsVersion
);

export const getIsTermsAcceptanceNeeded = createSelector(
  selectAppSettings,
  (appSettings) => appSettings.isTermsAcceptanceNeeded
);

export const getIsEmailVerificationRequired = createSelector(
  selectAppSettings,
  (appSettings) => appSettings.emailVerificationRequired
);

export const getIsEmailVerificationDismissible = createSelector(
  selectAppSettings,
  (appSettings) => appSettings.emailVerificationDismissible
);

export const getIsOrientationEnabled = createSelector(
  selectAppSettings,
  (appSettings) => !!appSettings.orientationEnabled
);

export const getAllOrientations = createSelector(
  selectAppSettings,
  (appSettings) => appSettings.orientations
);

export const getIsTinderPlusEnabled = createSelector(
  selectAppSettings,
  (appSettings) => appSettings.isTinderPlusEnabled
);

export const getIsGoldEnabled = createSelector(
  selectAppSettings,
  (appSettings) => appSettings.isGoldEnabled
);

export const getIsHubbleDebuggerEnabled = createSelector(
  selectAppSettings,
  (appSettings) => appSettings.isHubbleDebuggerEnabled
);

export const getIsPlatinumEnabled = createSelector(
  selectAppSettings,
  (appSettings) => appSettings.isPlatinumEnabled
);

export const getIsPremiumEnabled = createSelector(
  selectAppSettings,
  (appSettings) => appSettings.isGoldEnabled || appSettings.isPlatinumEnabled
);

export const getIsSubscriptionEnabled = createSelector(
  selectAppSettings,
  (appSettings) =>
    appSettings.isTinderPlusEnabled || appSettings.isGoldEnabled || appSettings.isPlatinumEnabled
);

export const getIsConsumablesEnabled = createSelector(
  selectAppSettings,
  (appSettings) => appSettings.isConsumablesEnabled
);

export const getThemeIdOrLight = createSelector(
  selectAppSettings,
  selectContext,
  (appSettings, context) => {
    const themeOverride: ThemeId | null = context.queryParams?.theme;
    return (
      themeOverride ??
      appSettings.themePreference ??
      appSettings.themeDevicePreference ??
      ThemeId.Light
    );
  }
);

export const getThemeId = createSelector(
  selectAppSettings,
  selectContext,
  (appSettings, context) => {
    const themeOverride: ThemeId | null = context.queryParams?.theme;
    return themeOverride ?? appSettings.themePreference ?? appSettings.themeDevicePreference;
  }
);

export const getThemePreferenceId = createSelector(
  selectAppSettings,
  (appSettings) => appSettings.themePreference
);

export const getAgeVerificationShowSessionOnboardingModal = createSelector(
  selectAppSettings,
  (app) => app.ageVerificationShowSessionOnboardingModal
);

export const getAgeVerificationIsSessionOnboardingSkippable = createSelector(
  selectAppSettings,
  (app) => app.ageVerificationIsSessionOnboardingSkippable
);

export const getAgeVerificationReason = createSelector(
  selectAppSettings,
  (app) => app.ageVerificationReason
);

export const getAgeVerificationStatus = createSelector(
  selectAppSettings,
  (app) => app.ageVerificationStatus
);

export const getIsAgeVerificationNeeded = createSelector(selectAppSettings, (app) =>
  NEEDED_STATUSES.has(app.ageVerificationStatus)
);

export const getNeedsBiometricConsent = createSelector(
  selectAppSettings,
  (appSettings) => appSettings.isBiometricConsentNeeded
);

export const getBiometricConsents = createSelector(
  selectAppSettings,
  (appSettings) => appSettings.biometricConsents
);

export const getBiometricLivenessState = createSelector(
  selectAppSettings,
  (appSettings) => appSettings.biometricLivenessState
);

export const getChatSuggestionConsents = createSelector(
  selectAppSettings,
  (appSettings) => appSettings.chatSuggestionConsents
);

export const getChatSuggestionConsentTitle = createSelector(
  selectAppSettings,
  (appSettings) => appSettings.chatSuggestionConsents[0]?.title ?? ''
);

export const getChatSuggestionConsentDescription = createSelector(
  selectAppSettings,
  (appSettings) => appSettings.chatSuggestionConsents[0]?.metadata[0][0]?.value ?? ''
);

export const getHasConsentedChatSuggestions = createSelector(selectAppSettings, (appSettings) =>
  appSettings.chatSuggestionConsents.length > 0
    ? appSettings.chatSuggestionConsents[0]?.consentStatus === CHAT_SUGGESTION_CONSENT_ACCEPTED
    : false
);

export const getChatSuggestionsConsentShown = createSelector(
  selectAppSettings,
  (appSettings) => appSettings.chatSuggestionConsentShown
);

export const getMerchandisingFeatures = createSelector(selectAppSettings, (appSettings) =>
  appSettings.merchandisingFeatures?.length
    ? appSettings.merchandisingFeatures
    : DEFAULT_MERCHANDISING_CARDS
);

export const getNeedsMessageConsent = createSelector(
  selectAppSettings,
  (appSettings) => appSettings.isMessageConsentNeeded
);

export const getMessageConsents = createSelector(
  selectAppSettings,
  (appSettings) => appSettings.messageConsents
);

export const getPaywallFeatures = createSelector(
  selectAppSettings,
  (appSettings) => appSettings.paywallFeatures
);

export const getPlatinumPaywallFeatures = createSelector(
  selectAppSettings,
  (appSettings) => appSettings.platinumPaywallFeatures
);

export const getAppStartInfo = createSelector(
  selectAppSettings,
  (appSettings) => appSettings.appStartInfo || DEFAULT_CHECK
);

export const getDistanceUnit = createSelector(
  selectAppSettings,
  (appSettings) => appSettings.distanceUnit
);
